import React, { useCallback, useState, useEffect } from 'react';

import {
  Calendar,
  Users,
  Eye,
  Edit,
  Trash2,
  Plus,
  Bell,
  MessageSquare,
  Shield,
  RefreshCw,
  Tag,
  Globe,
  Zap,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AnnouncementForm } from '../../components/announcements/AnnouncementForm';
import { Pagination } from '../../components/common';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/modals/Modal';
import { ROUTES } from '../../constants';
import {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  approveAnnouncement as approveAnnouncementRequest,
  broadcastAnnouncement as broadcastAnnouncementRequest,
  AnnouncementFilters,
} from '../../services/announcementsService';
import {
  Announcement,
  AnnouncementCreateFormData,
  AnnouncementUpdateFormData,
} from '../../types';


interface SearchForm {
  search: string;
  status_filter: string;
}

export const AnnouncementDashboard: React.FC = () => {
  const navigate = useNavigate();

  // State Management
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AnnouncementFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] =
    useState<Announcement | null>(null);
  const [deletingAnnouncement, setDeletingAnnouncement] =
    useState<Announcement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Search form
  const { register, handleSubmit } = useForm<SearchForm>({
    defaultValues: {
      search: filters.search || '',
      status_filter: filters.status || '',
    },
  });

  // Constants
  const ITEMS_PER_PAGE = 20;
  const PRIORITY_COLORS = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };

  const STATUS_COLORS = {
    draft: 'bg-gray-100 text-gray-800',
    pending_approval: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    scheduled: 'bg-purple-100 text-purple-800',
    sent: 'bg-indigo-100 text-indigo-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
  };

  const TYPE_ICONS = {
    general: MessageSquare,
    emergency: null, // AlertTriangle removed
    event: Calendar,
    academic: Users,
    policy: Shield,
    reminder: Bell,
  };

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getAnnouncements({
        ...filters,
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
      });
      setAnnouncements(data.data);
      setTotalCount(data.pagination.total);
      setError(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load announcements';
      setAnnouncements([]);
      setTotalCount(0);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  const handleBroadcastAnnouncement = async (announcementId: string) => {
    try {
      await broadcastAnnouncementRequest(announcementId);
      await fetchAnnouncements();
      toast.success('Announcement queued for broadcast');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Broadcast failed');
    }
  };

  const handleApproveAnnouncement = async (
    announcementId: string,
    decision: 'approved' | 'rejected'
  ) => {
    try {
      await approveAnnouncementRequest(announcementId, decision);
      await fetchAnnouncements();
      toast.success(`Announcement ${decision}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Approval failed');
    }
  };

  // Effects
  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    // Set up auto-refresh for pending approvals and scheduled announcements
    const interval = setInterval(() => {
      fetchAnnouncements();
    }, 30000); // Refresh every 30 seconds

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchAnnouncements]);

  // Computed Values
  const pendingApprovals = announcements.filter(
    a => a.status === 'pending_approval'
  ).length;
  const scheduledAnnouncements = announcements.filter(
    a => a.status === 'scheduled'
  ).length;
  const emergencyAnnouncements = announcements.filter(
    a => a.type === 'emergency' && a.status !== 'sent'
  ).length;

  // Pagination helpers (reuse common Pagination component)
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const pagination = {
    page: currentPage,
    per_page: ITEMS_PER_PAGE,
    total: totalCount,
    pages: Math.ceil(totalCount / ITEMS_PER_PAGE) || 0,
    has_next: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE),
    has_prev: currentPage > 1,
    next_page:
      currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE)
        ? currentPage + 1
        : undefined,
    prev_page: currentPage > 1 ? currentPage - 1 : undefined,
  };

  // Event Handlers

  const handleSearch = (data: SearchForm) => {
    setFilters(prev => ({
      ...prev,
      search: data.search,
      status: data.status_filter,
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const handleCreateAnnouncement = () => {
    setEditingAnnouncement(null);
    setIsModalOpen(true);
  };

  const handleEditAnnouncement = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setIsModalOpen(true);
  };

  const handleDeleteAnnouncement = (announcement: Announcement) => {
    setDeletingAnnouncement(announcement);
  };

  const handleFormSubmit = async (
    data: AnnouncementCreateFormData | AnnouncementUpdateFormData
  ) => {
    try {
      setIsSubmitting(true);

      if (editingAnnouncement) {
        // Update existing announcement
        await updateAnnouncement(
          editingAnnouncement.id,
          data as AnnouncementUpdateFormData
        );
        toast.success('Announcement updated successfully');
      } else {
        // Create new announcement
        await createAnnouncement(data as AnnouncementCreateFormData);
        toast.success('Announcement created successfully');
      }

      await fetchAnnouncements();
      setIsModalOpen(false);
      setEditingAnnouncement(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingAnnouncement) return;

    try {
      setIsSubmitting(true);
      await deleteAnnouncement(deletingAnnouncement.id);
      toast.success('Announcement deleted successfully');

      await fetchAnnouncements();
      setDeletingAnnouncement(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAnnouncement(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return null; // CheckCircle removed
      case 'failed':
        return null; // XCircle removed
      case 'pending_approval':
        return <Calendar className="w-4 h-4 text-blue-600" />;
      case 'scheduled':
        return <Calendar className="w-4 h-4 text-purple-600" />;
      default:
        return <Bell className="w-4 h-4 text-gray-600" />;
    }
  };

  // Render Functions
  const renderAnnouncementCard = (announcement: Announcement) => {
    const TypeIcon = TYPE_ICONS[announcement.type];

    return (
      <div
        key={announcement.id}
        className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            {TypeIcon && <TypeIcon className="w-5 h-5 text-gray-500" />}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                {announcement.title}
              </h3>
              <p className="text-sm text-gray-500">
                Created {formatDate(announcement.created_at)}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {announcement.contains_pii && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <Shield className="w-3 h-3 mr-1" />
                PII
              </span>
            )}
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[announcement.priority]}`}
            >
              {announcement.priority.toUpperCase()}
            </span>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[announcement.status]}`}
            >
              {getStatusIcon(announcement.status)}
              <span className="ml-1">
                {announcement.status.replace('_', ' ').toUpperCase()}
              </span>
            </span>
          </div>
        </div>

        {/* Content Preview */}
        <div className="mb-4">
          <p className="text-gray-700 line-clamp-2">
            {announcement.summary || announcement.content}
          </p>
        </div>

        {/* Targeting Info */}
        <div className="flex items-center space-x-4 mb-4 text-sm text-gray-500">
          <div className="flex items-center space-x-1">
            <Users className="w-4 h-4" />
            <span>
              {announcement.total_recipients.toLocaleString()} recipients
            </span>
          </div>

          {announcement.target_roles && (
            <div className="flex items-center space-x-1">
              <Tag className="w-4 h-4" />
              <span>{announcement.target_roles.join(', ')}</span>
            </div>
          )}

          <div className="flex items-center space-x-1">
            <Globe className="w-4 h-4" />
            <span>{announcement.channels.join(', ')}</span>
          </div>
        </div>

        {/* Analytics */}
        {announcement.status === 'sent' && (
          <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-900">
                {announcement.delivery_count}
              </p>
              <p className="text-xs text-gray-500">Delivered</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-900">
                {announcement.read_count}
              </p>
              <p className="text-xs text-gray-500">Opened</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-900">
                {announcement.engagement_count}
              </p>
              <p className="text-xs text-gray-500">Engaged</p>
            </div>
          </div>
        )}

        {/* Scheduling Info */}
        {announcement.scheduled_for && (
          <div className="flex items-center space-x-2 mb-4 p-2 bg-purple-50 rounded-lg">
            <Calendar className="w-4 h-4 text-purple-600" />
            <span className="text-sm text-purple-800">
              Scheduled for {formatDate(announcement.scheduled_for)}
            </span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() =>
                navigate(
                  ROUTES.ANNOUNCEMENT_DETAIL.replace(':id', announcement.id)
                )
              }
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <Eye className="w-4 h-4" />
              <span>View</span>
            </button>

            {announcement.status !== 'sent' && (
              <button
                onClick={() => handleEditAnnouncement(announcement)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-900 transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {announcement.status === 'pending_approval' && (
              <>
                <button
                  onClick={() =>
                    handleApproveAnnouncement(announcement.id, 'approved')
                  }
                  className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                >
                  {/* CheckCircle removed */}
                  <span>Approve</span>
                </button>
                <button
                  onClick={() =>
                    handleApproveAnnouncement(announcement.id, 'rejected')
                  }
                  className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                >
                  {/* XCircle removed */}
                  <span>Reject</span>
                </button>
              </>
            )}

            {(announcement.status === 'approved' ||
              announcement.status === 'scheduled') && (
              <button
                onClick={() => handleBroadcastAnnouncement(announcement.id)}
                className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
              >
                <Zap className="w-4 h-4" />
                <span>Broadcast</span>
              </button>
            )}

            {announcement.status !== 'sent' && (
              <button
                onClick={() => handleDeleteAnnouncement(announcement)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-900 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main Render
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Bell className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Announcement Dashboard
                </h1>
                <p className="text-sm text-gray-500">
                  Manage and broadcast announcements
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Create Announcement Button */}
              <button
                onClick={handleCreateAnnouncement}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Create Announcement</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Bar for Pending Items */}
      {(pendingApprovals > 0 ||
        scheduledAnnouncements > 0 ||
        emergencyAnnouncements > 0) && (
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center space-x-6 text-sm">
              {pendingApprovals > 0 && (
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-yellow-600" />
                  <span className="text-yellow-800 font-medium">
                    {pendingApprovals} announcement
                    {pendingApprovals !== 1 ? 's' : ''} pending approval
                  </span>
                </div>
              )}

              {scheduledAnnouncements > 0 && (
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-purple-600" />
                  <span className="text-purple-800 font-medium">
                    {scheduledAnnouncements} scheduled announcement
                    {scheduledAnnouncements !== 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {emergencyAnnouncements > 0 && (
                <div className="flex items-center space-x-2">
                  {/* AlertTriangle removed */}
                  <span className="text-red-800 font-medium">
                    {emergencyAnnouncements} emergency announcement
                    {emergencyAnnouncements !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="py-8">
        <div className="space-y-6">
          {/* Search and Filters */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <form
              onSubmit={handleSubmit(handleSearch)}
              className="space-y-4 lg:space-y-0"
            >
              {/* Mobile/Tablet Layout - Two Rows */}
              <div className="flex flex-col space-y-4 lg:hidden">
                {/* Row 1: Search Input and Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <input
                      {...register('search')}
                      type="text"
                      placeholder="Search announcements by title, content, type, or target roles..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                      defaultValue={filters.search || ''}
                    />
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                      className="min-h-[44px] flex-1 sm:flex-none sm:px-6"
                    >
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                    {(filters.search || filters.status) && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={clearFilters}
                        className="min-h-[44px] px-4"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Row 2: Filter Dropdowns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="status-filter-mobile"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Status
                    </label>
                    <select
                      {...register('status_filter')}
                      id="status-filter-mobile"
                      className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                    >
                      <option value="">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="pending_approval">Pending Approval</option>
                      <option value="approved">Approved</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="sent">Sent</option>
                      <option value="failed">Failed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Desktop Layout - All Inline */}
              <div className="hidden lg:flex lg:items-end lg:gap-4">
                <div className="flex-1 min-w-0">
                  <input
                    {...register('search')}
                    id="search-desktop"
                    type="text"
                    placeholder="Search announcements by title, content, type, or target roles..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    defaultValue={filters.search || ''}
                  />
                </div>

                <div className="w-48">
                  <select
                    {...register('status_filter')}
                    id="status-filter-desktop"
                    className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                    className="min-h-[44px] px-6"
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                  {(filters.search || filters.status) && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={clearFilters}
                      className="min-h-[44px] px-4"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
              <span className="ml-2 text-gray-600">
                Loading announcements...
              </span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                {/* XCircle removed */}
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Announcements List */}
          {!loading && !error && (
            <div className="space-y-4">
              {announcements.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No announcements found
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Get started by creating your first announcement.
                  </p>
                  <button
                    onClick={handleCreateAnnouncement}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors mx-auto"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create Announcement</span>
                  </button>
                </div>
              ) : (
                <>
                  {announcements.map(renderAnnouncementCard)}

                  {/* Pagination */}
                  {totalCount > 0 && (
                    <div className="bg-white px-4 py-3 border border-gray-200 rounded-lg">
                      <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'
        }
        size="2xl"
      >
        <AnnouncementForm
          announcement={editingAnnouncement || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseModal}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingAnnouncement}
        onClose={() => setDeletingAnnouncement(null)}
        title="Delete Announcement"
        size="md"
      >
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">
                Are you sure?
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  This action cannot be undone. This will permanently delete the
                  announcement
                  {deletingAnnouncement && (
                    <>
                      {' '}
                      <strong>"{deletingAnnouncement.title}"</strong>
                    </>
                  )}
                  .
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => setDeletingAnnouncement(null)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              loading={isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Announcement
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
